import { Modal } from "../../../../../components/modal-base";
import { ModalContent } from "./styles";
import { useEffect, useState } from "react";
import { CreditService } from "../../../services";
import { toast } from "react-toastify";
import { useSocketContext } from "../../../../../contexts";
import { ScenariosService } from "@/modules/scenarios/services";
import { TScenario } from "@/modules/scenarios/models";
import { PaymentStep } from "@/utils/enum";
import {
  AddressContent,
  ConsultAmountInput,
  ErrorContent,
  LoadingContent,
  PaymentContent,
  SuccessContent
} from "./ContentComponents";
import { ICustomPlan } from "@/modules/credits/models";

interface IConfirmPurchase {
  modal: boolean;
  creditsDetailsParam: {
    amount: number;
    price: string;
  };
  toggleModal: () => void;
  selectedAccount: number;
  customPlan: ICustomPlan | null;
}

export const ConfirmPurchase: React.FC<IConfirmPurchase> = ({
  modal,
  creditsDetailsParam,
  toggleModal,
  selectedAccount,
  customPlan
}) => {
  const { creditsResponse } = useSocketContext();
  const [currentStep, setCurrentStep] = useState<PaymentStep>(
    PaymentStep.LOADING
  );
  const [baseImg, setBaseImg] = useState<string>("");
  const [copyCode, setCopyCode] = useState<string>("");
  const [isOpenSetConsult, setIsOpenSetConsult] = useState<boolean>(false);
  const [creditsDetails, setCreditsDetails] = useState<{
    amount: number;
    price: string;
  }>({
    amount: 0,
    price: ""
  });
  const [, setScenarios] = useState<TScenario[]>([]);
  const [paymentId, setPaymentId] = useState<string>("");
  const [paymentValue, setPaymentValue] = useState<number>(0);
  const cardSeventyFiveAmount = 75;
  const minOrder = customPlan?.minCredits || cardSeventyFiveAmount;
  SuccessContent;

  const fetchData = async () => {
    const response = await ScenariosService.getScenarios(1, selectedAccount);
    if (response && response.data) {
      setScenarios(response.data as TScenario[]);
    } else {
      setScenarios([]);
      toast.error("Erro ao consultar Critérios");
    }
  };

  useEffect(() => {
    if (selectedAccount) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount]);

  useEffect(() => {
    const { amount, price } = creditsDetailsParam;
    if (customPlan || amount >= cardSeventyFiveAmount) {
      setIsOpenSetConsult(true);
    } else {
      setIsOpenSetConsult(false);
      if (selectedAccount) {
        confirmPurchase(amount, price);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creditsDetailsParam]);

  // Ao receber confirmação via socket, atualiza para sucesso
  useEffect(() => {
    if (creditsResponse && creditsResponse.creditsAdded) {
      setCurrentStep(PaymentStep.SUCCESS);
    }
  }, [creditsResponse]);

  const confirmPurchase = async (amount: number, price: string) => {
    setIsOpenSetConsult(false);
    try {
      setCreditsDetails({ amount, price });
      setCurrentStep(PaymentStep.LOADING);
      const response = await CreditService.createPayment({
        price: Number(price) * amount,
        amount: amount,
        selectedAccount
      });

      if (response.success) {
        const { encodedImage, payload, paymentId } = response.data!;
        setBaseImg(encodedImage);
        setCopyCode(payload);
        setPaymentId(paymentId);
        setPaymentValue(Number(price) * amount);
        setCurrentStep(PaymentStep.PAYMENT);
      } else {
        console.error("Erro ao confirmar a compra:", response);
        setCurrentStep(PaymentStep.ERROR);
      }
    } catch (error) {
      console.error("Erro inesperado durante a confirmação da compra:", error);
      setCurrentStep(PaymentStep.ERROR);
    }
  };

  const handleCloseModal = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    setIsOpenSetConsult(
      !!customPlan || creditsDetailsParam.amount === cardSeventyFiveAmount
    );
    toggleModal();
    setCurrentStep(PaymentStep.PAYMENT);
  };
  const handleTryAgain = () => {
    const { amount, price } = creditsDetails;
    confirmPurchase(amount, price);
  };

  return (
    <Modal.Root maxWidth="600px" open={modal} onClose={handleCloseModal}>
      <Modal.Header justifyContent="end">
        <Modal.HeaderText
          text={
            isOpenSetConsult ? "Escolha a quantidade" : "Realizar Pagamento"
          }
        />
        <Modal.Icon
          icon="Close"
          cursor="pointer"
          onClick={handleCloseModal}
          iconColor="black"
        />
      </Modal.Header>
      <Modal.Content>
        <ModalContent style={{ marginBottom: "4vh" }}>
          {isOpenSetConsult ? (
            <ConsultAmountInput
              confirmPurchase={confirmPurchase}
              minOrder={minOrder}
              creditsDetails={creditsDetailsParam}
            />
          ) : (
            <>
              {currentStep === PaymentStep.LOADING && <LoadingContent />}
              {currentStep === PaymentStep.PAYMENT && (
                <PaymentContent
                  baseImg={baseImg}
                  copyCode={copyCode}
                  customPlan={customPlan}
                  creditsDetails={creditsDetails}
                />
              )}
              {currentStep === PaymentStep.SUCCESS && (
                <SuccessContent
                  toggleModal={toggleModal}
                  setCurrentStep={setCurrentStep}
                  paymentId={paymentId}
                  paymentValue={paymentValue}
                  selectedAccount={selectedAccount}
                />
              )}
              {currentStep === PaymentStep.ERROR && (
                <ErrorContent
                  toggleModal={toggleModal}
                  setCurrentStep={setCurrentStep}
                  handleTryAgain={handleTryAgain}
                />
              )}
              {currentStep === PaymentStep.ADDRESS && (
                <AddressContent
                  toggleModal={toggleModal}
                  setCurrentStep={setCurrentStep}
                  paymentId={paymentId}
                  paymentValue={paymentValue}
                  selectedAccount={selectedAccount}
                />
              )}
            </>
          )}
        </ModalContent>
      </Modal.Content>
    </Modal.Root>
  );
};
